import React, { useState, /*useRef,*/ useEffect, useRef } from 'react';
import styles from './MedicalCentersSearchResults.module.scss';
import {
  Configure,
  Index,
  InstantSearch,
  UseInfiniteHitsProps,
  //  UseSortByProps,
  useInfiniteHits,
  useInstantSearch,
  //  useSortBy,
  useStats,
} from 'react-instantsearch';
import type { BaseHit, Hit } from 'instantsearch.js';
import { MEDICALCENTERS, MEDICALCENTERS_SORT, leumitSearchClient, localAttributes } from 'assets/constants';
import { MedicalCenterResultType, objectTypeProps } from 'types';
import { checkIfParamExist, getAllClassNames, getParams } from 'helpers';
import strings from "assets/strings";
import Text from "components/text/Text";
import getRouting from './routing';
import { MedicalCenterRefinements } from './MedicalCenterRefinements/MedicalCenterRefinements';
import { MedicalCenterResult } from './MedicalCenterResult';
import { VirtualRefinementList } from 'components/virtualRefinement/VirtualRefinmentList';
import { FilterButton } from "components/buttons/filterButton/FilterButton";
import Popup from "components/popup/Popup";
import { CustomSortDropdown } from "components/customSortDropdown/CustomSortDropdown";
//import { Filter } from "assets/icons";
import Banners from 'components/banners/Banners';
import { VirtualQuery } from 'components/virtualQuery/VirtualQuery';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { VirtualSortBy } from 'components/virtualSortBy/virtualSortBy';
import { CustomSortBy } from 'components/customSortBy/CustomSortBy';
//import { Loader } from "assets/images";

const leumitCenterRouting = getRouting(MEDICALCENTERS, 4);
const opticaRouting = getRouting(MEDICALCENTERS, 5);
const emergencyRouting = getRouting(MEDICALCENTERS, 6);
const pharmacyRouting = getRouting(MEDICALCENTERS, 7);
const womenEmergencyRouting = getRouting(MEDICALCENTERS, 8);
const dentalRouting = getRouting(MEDICALCENTERS, 9);

function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { results } = useInstantSearch();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className="noMessagesWrapper">
        {/* <SvgGenerator
          name="Magnifier"
          className="magnifierIcon"
          objectName="medicalCenterSearchResults"
        /> */}
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
}

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);
  return (
    <div
      className={styles.noMessagesWrapper}
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}>
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}

export function MedicalCenterSearchResults(props: objectTypeProps) {
  const { objectType } = props;
  let routing: any;
  switch (objectType) {
    case 4:
      routing = leumitCenterRouting;
      break;
    case 5:
      routing = opticaRouting;
      break;
    case 6:
      routing = emergencyRouting;
      break;
    case 7:
      routing = pharmacyRouting;
      break;
    case 8:
      routing = womenEmergencyRouting;
      break;
    case 9:
      routing = dentalRouting;
      break;
    default:
      break;
  }

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [isBanners, setIsBanners] = useState(false);
  const [currentSortBy, setCurrentSortBy] = useState(MEDICALCENTERS);
  const popupRef = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState(0); // State to force re-render

  function GeoInfiniteHitsComponent(props: UseInfiniteHitsProps) {
    const { hits, showMore, isLastPage } = useInfiniteHits(props);
    return (
      <div>
        {hits.map((hit: Hit<BaseHit>) => {
          return <MedicalCenterResult hit={hit as MedicalCenterResultType} objectType={objectType} />;
        })
        }
        {!isLastPage && <button className='ais-InfiniteHits-loadMore' onClick={showMore} disabled={isLastPage}>{strings.globalStrings.moreResults}</button>}
      </div>
    )

  }

  function InfiniteHitsComponent(props: UseInfiniteHitsProps) {
    const { hits, showMore, isLastPage } = useInfiniteHits(props);
    const [nextFocusIndex, setCurrentFocusIndex] = React.useState(0);
    const medicalCenterResultRef = useRef<HTMLDivElement>(null);

    const handleShowMore = () => {
      showMore();
      setCurrentFocusIndex(nextFocusIndex + 20);
      medicalCenterResultRef.current?.focus();
    }

    return (
      hits.length > 0 ? <div className='animate'>
        {hits.map((hit: Hit<BaseHit>, index) => {
          return <MedicalCenterResult hit={hit as MedicalCenterResultType} objectType={objectType} key={`${hit.objectID}_${index}`} ref={index == nextFocusIndex ? medicalCenterResultRef : null} />;
        })
        }
        {!isLastPage && <button className='ais-InfiniteHits-loadMore' onClick={handleShowMore} disabled={isLastPage}>{strings.globalStrings.moreResults}</button>}
      </div> : null
    )
  }

  type TopRefinementProps = {
    setCurrentSortBy: React.Dispatch<React.SetStateAction<string>>;
  };

  const TopRefinement: React.FC<TopRefinementProps> = ({ setCurrentSortBy }) => {
    const storedHitsResults = localStorage.getItem('hitsResults') || '';
    const totalHits = storedHitsResults ? JSON.parse(storedHitsResults).reduce((acc: number, res: any) => acc + res.total, 0) : 0;

    return (
      (totalHits && totalHits > 0) ? <>
        <div className={styles.container}>
          <div className={styles.divResults}>
            <Stats />
            {checkIfParamExist('loc') && !isMobile ? <>
              <Text
                className={styles.text}
                text={`${strings.globalStrings.sortBy}`} />
              <CustomSortBy
                items={[
                  { label: "שם", value: MEDICALCENTERS },
                  { label: "מרחק", value: MEDICALCENTERS_SORT },
                ]} onRefine={setCurrentSortBy} />
            </> : null}
          </div>
        </div>
        {strings.medicalCentersSearchResults.topExplanation &&
          <div className={getAllClassNames([
            styles.divTopExpaination,
          ])}>
            <SvgGenerator name="Exclamation" className="exclamationIcon" objectName="medicalCenterSearchResults" />
            <Text className="text" text={strings.medicalCentersSearchResults.topExplanation} />
          </div>
        }
      </> : null
    );
  };
  const Stats = () => {
    const { nbHits } = useStats();
    if (!nbHits)
      return null;
    return (
      nbHits === 1 ?
        <Text
          className={styles.title}
          text={strings.globalStrings.foundOneResult} /> :
        <Text
          className={styles.title}
          text={`${strings.medicalCentersSearchResults.found} ${nbHits} ${strings.medicalCentersSearchResults.results}`}
        />
    );
  };

  const RenderResults = ({ currentSortBy }: { currentSortBy: string }) => {
    const [hitsResults, setHitsResults] = useState<{ indexName: string, total: number }[]>([
      { indexName: 'MEDICALCENTERS_INDEX', total: 0 },
      { indexName: 'exact', total: 0 },
      { indexName: 'geo', total: 0 }
    ]);
    const [medicalHits, setMedicalCenterHits] = useState(0);
    const [exactHits, setExactHits] = useState(0);
    const [geoHits, setGeoHits] = useState(0);
    const [isFinalResults, setIsFinalResults] = useState(false);
    const { scopedResults } = useInstantSearch();

    useEffect(() => {
      // This function will be called whenever scopedResults changes.
      function calculateExactStats() {
        let hitsIndexes = [
          { indexName: 'MEDICALCENTERS_INDEX', total: 0 },
          { indexName: 'exact', total: 0 },
          { indexName: 'geo', total: 0 }
        ];

        let medicalCenterIndex = scopedResults.filter(i => i.indexId === MEDICALCENTERS);
        let exactindex = scopedResults.filter(i => i.indexId === 'exact');
        let geoindex = scopedResults.filter(i => i.indexId === 'geo');

        if (medicalCenterIndex.length) {
          const newHits = medicalCenterIndex[0].results?.nbHits || 0;

          if (hitsIndexes[0].total !== newHits) {
            setMedicalCenterHits(newHits);
            hitsIndexes[0].total = newHits;
          }
        }

        if (exactindex.length) {
          const newExactHits = exactindex[0].results?.nbHits || 0;

          if (hitsIndexes[1].total !== newExactHits) {
            setExactHits(newExactHits);
            hitsIndexes[1].total = newExactHits;
          }
        }
        if (geoindex.length) {
          const newGeoHits = geoindex[0].results?.nbHits || 0;

          if (hitsIndexes[2].total !== newGeoHits) {
            setGeoHits(newGeoHits);
            hitsIndexes[2].total = newGeoHits;
          }
        }

        return hitsIndexes;
      }

      // Calculate the exact stats and update the state.
      //const newHitsResults = calculateExactStats();
      //remove loc and r from url and keep the rest

      if (hitsResults[1].total >= 5) {
        const url = new URL(window.location.href);
        url.searchParams.delete('loc');
        url.searchParams.delete('r');
        window.history.replaceState({}, document.title, url.toString());
        setKey(prevKey => prevKey + 1); // Force re-render
        //window.location.href = url.toString();
      }


      if (scopedResults.length > 0) {
        const allResultsFetched = scopedResults.every(
          result => result.results?.hits && !result.results?.exhaustiveNbHits
        );

        if (allResultsFetched !== isFinalResults) {
          setIsFinalResults(allResultsFetched);
        }
      }

      const newHitsResults = calculateExactStats();
      if (newHitsResults !== hitsResults) {
        setHitsResults(newHitsResults);
      }
      // setTimeout(() => setIsLoading(false), 20000); // Delay the loading state to prevent flickering
      // //setIsLoading(false); // Mark loading as complete when search is finalized

    }, [scopedResults, medicalHits, exactHits, geoHits]); // Only re-run the effect if scopedResults changes


    localStorage.setItem('hitsResults', JSON.stringify(hitsResults));

    // if (status !== 'idle' && !isFinalResults) {
    //   // Render a loading spinner or placeholder while waiting for results
    //   return (
    //     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }} >
    //       <img src={Loader} />
    //     </div>
    //   );
    // }

    if (!checkIfParamExist('loc')) {
      return <InfiniteHitsComponent />
    }
    else if (hitsResults[1].total < 5) {
      return <><Index indexName={MEDICALCENTERS} indexId="exact">
        <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 10000} />
        <VirtualRefinementList attribute="cityCode" />
        <GeoInfiniteHitsComponent />
        {hitsResults[2].total > 0 && <div className="resultsAround">
          <SvgGenerator name="Exclamation" className="exclamationIcon" objectName="medicalCenterSearchResults" />
          {/* <Exclamation className="exclamationIcon" /> */}
          <Text text={hitsResults[1].total == 0 ? strings.globalStrings.foundResultsAroundLocation : strings.globalStrings.foundMoreResultsAroundLocation} />
        </div>
        }
      </Index>
        <Index indexName={MEDICALCENTERS} indexId="geo">
          <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 10000} />
          <VirtualSortBy items={[{ label: "שם", value: MEDICALCENTERS }, { label: "מרחק", value: MEDICALCENTERS_SORT }]} sortBy={currentSortBy} />
          <GeoInfiniteHitsComponent />
        </Index>
      </>
    }
    else {
      return (
        <>
          <Index indexName={MEDICALCENTERS} indexId="exact">
            <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 10000} />
            <VirtualRefinementList attribute="cityCode" />
            <GeoInfiniteHitsComponent />
          </Index>
        </>
      );
    }

  }
  useEffect(() => {
    //setIsMobile(/iPhone|Android|mobile/i.test(navigator.userAgent));
    if (window.BANNERS_emergencySearchResults) setIsBanners(true);
  }, []);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 992);
  };

  useEffect(() => {
    // Set up the event listener
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };

  }, []);

  const handleToggleVisibility = () => {
    if (popupRef.current) {
      popupRef.current.classList.toggle('show');
    }
  };

  return (
    <div className="row two-col-layout ais-InstantSearch" key={key}>
      <InstantSearch
        indexName={MEDICALCENTERS}
        searchClient={leumitSearchClient}
        routing={routing}
      >
        <Configure snippetEllipsisText="…" hitsPerPage={20} />
        {isMobile &&
          <Popup
            ref={popupRef}
            onClose={() => handleToggleVisibility()}
          >
            <MedicalCenterRefinements objectType={objectType} />
          </Popup>
        }
        {!isMobile && <MedicalCenterRefinements objectType={objectType} />
        }

        <div className={`col-12 ${styles.refineWrapper} ais-SearchResults`}>
          {isMobile &&
            <div className={getAllClassNames([styles.divMobileTop])}>
              <FilterButton onClick={() => handleToggleVisibility()} text={strings.globalStrings.filterBy} />
              {checkIfParamExist('loc') && <CustomSortDropdown items={[
                { label: "שם", value: MEDICALCENTERS, items: [] },
                { label: "מרחק", value: MEDICALCENTERS_SORT, items: [] },
              ]}
                onSortChange={setCurrentSortBy}
              />
              }
            </div>
          }
          <TopRefinement setCurrentSortBy={setCurrentSortBy} />
          {isBanners && (objectType == 6 || objectType == 8) ? <Banners banners={window.BANNERS_emergencySearchResults.banners} /> : null}
          <VirtualRefinementList attribute="cityCode" />
          <VirtualRefinementList attribute="dayOfWeek" />
          <VirtualQuery query={localAttributes.localOuterServiceQuery} />
          <NoResultsBoundary fallback={<NoResults />}>
            <RenderResults currentSortBy={currentSortBy} />
          </NoResultsBoundary>
        </div>
      </InstantSearch>
    </div>
  );
}
